import { getActivePinia } from "pinia";
import { useIndoStore } from "@/stores/indo";

/**
 * Helpers de formato monetario para facturación.
 *
 * Los importes que llegan de SAP vienen como string con coma decimal y sin
 * separador de miles (p.ej. "1234,56"), aunque también pueden llegar con punto
 * decimal ("1234.56"). Estos helpers los normalizan a número y los devuelven en
 * formato español: separador de miles "." y separador decimal "," (p.ej.
 * "1.234,56"), añadiendo opcionalmente el símbolo de la moneda del cliente
 * (`MONEDA_CLI` en DatosConfig.DETALLES: EUR → "€", MAD → "DH").
 */

const DEFAULT_CURRENCY = "EUR";

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: "€",
  MAD: "DH",
};

/**
 * Normaliza el código de moneda que llega de SAP (`MONEDA_CLI`). Si viene
 * vacío, null o con un código desconocido devuelve EUR.
 */
export function normalizeCurrencyCode(code: unknown): string {
  const normalized = typeof code === "string" ? code.trim().toUpperCase() : "";
  return normalized in CURRENCY_SYMBOLS ? normalized : DEFAULT_CURRENCY;
}

/** Símbolo de una moneda ("EUR" → "€", "MAD" → "DH"); desconocida → "€". */
export function getCurrencySymbol(code: unknown): string {
  return CURRENCY_SYMBOLS[normalizeCurrencyCode(code)];
}

// Código completo (sin letras alrededor), aunque vaya pegado al número: "12,00EUR".
const CURRENCY_CODE_RE = new RegExp(`(?<![a-z])(${Object.keys(CURRENCY_SYMBOLS).join("|")})(?![a-z])`, "gi");

/**
 * Sustituye los códigos de moneda que SAP incluye en textos de importe por su
 * símbolo, sin tocar el número: "123,45 EUR" → "123,45 €", "80,00 MAD" → "80,00 DH".
 */
export function replaceCurrencyCode(text: unknown): string {
  if (text === null || text === undefined) return "";
  return String(text).replace(CURRENCY_CODE_RE, (code) => getCurrencySymbol(code));
}

/** Símbolo de la moneda del cliente logueado (EUR si no hay store/perfil). */
function currentCurrencySymbol(): string {
  const fallback = CURRENCY_SYMBOLS[DEFAULT_CURRENCY];
  return getActivePinia() ? useIndoStore().simboloMoneda ?? fallback : fallback;
}

/**
 * Convierte un importe (number o string en cualquier formato razonable) a
 * número. Devuelve null si no se puede interpretar.
 *
 * Reglas de parseo para strings:
 * - Se ignoran el símbolo de moneda ("€", "DH"), espacios y cualquier carácter no numérico.
 * - Si aparecen "." y "," el separador que ocurre en último lugar es el
 *   decimal y el otro se trata como separador de miles ("1.234,56" y
 *   "1,234.56" → 1234.56).
 * - Si solo aparece uno de los dos, se interpreta como separador decimal
 *   ("1234,56" y "1234.56" → 1234.56).
 */
export function parsePrecio(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  let str = String(value).trim();
  if (str === "") return null;

  const negative = /-/.test(str);
  // Dejar solo dígitos y separadores
  str = str.replace(/[^0-9.,]/g, "");
  if (str === "") return null;

  const lastComma = str.lastIndexOf(",");
  const lastDot = str.lastIndexOf(".");

  let normalized: string;
  if (lastComma > lastDot) {
    // La coma es el separador decimal
    normalized = str.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    // El punto es el separador decimal
    normalized = str.replace(/,/g, "");
  } else {
    normalized = str;
  }

  const n = parseFloat(normalized);
  if (Number.isNaN(n)) return null;
  return negative ? -n : n;
}

export interface FormatPrecioOptions {
  /** Añade el símbolo de moneda al final (por defecto true). */
  withSymbol?: boolean;
  /** Símbolo a usar (por defecto el de la moneda del cliente, "€" si no hay). */
  symbol?: string;
  /** Valor a devolver cuando el importe no es interpretable (por defecto "-"). */
  fallback?: string;
  /** Número de decimales (por defecto 2). */
  decimals?: number;
}

// Crear un Intl.NumberFormat es caro y formatPrecio se llama por celda/fila:
// se cachea uno por número de decimales.
const formatters = new Map<number, Intl.NumberFormat>();

function getFormatter(decimals: number): Intl.NumberFormat {
  let formatter = formatters.get(decimals);
  if (!formatter) {
    formatter = new Intl.NumberFormat("es-ES", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      // "always" fuerza el separador de miles también en números de 4 cifras
      // (es-ES por defecto no agrupa 1234 → "1234"; aquí queremos "1.234").
      useGrouping: "always",
    });
    formatters.set(decimals, formatter);
  }
  return formatter;
}

/**
 * Formatea un importe al formato español (miles "." y decimal ",") añadiendo
 * el símbolo de la moneda del cliente por defecto. Si el valor no es interpretable devuelve el
 * `fallback` (sin símbolo).
 *
 * Ejemplos: formatPrecio("1234,5") → "1.234,50 €"; formatPrecio("") → "-".
 */
export function formatPrecio(value: unknown, options: FormatPrecioOptions = {}): string {
  const { withSymbol = true, fallback = "-", decimals = 2, symbol } = options;

  const n = parsePrecio(value);
  if (n === null) return fallback;

  const formatted = getFormatter(decimals).format(n);

  return withSymbol ? `${formatted} ${symbol ?? currentCurrencySymbol()}` : formatted;
}
